import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { HelperIcon } from '@wg/wows-react-uikit';

import { t } from '~/helpers/localization';
import { getAssignableRoles, hasPermission, PERMISSIONS } from '~/roles';
import { useAppDispatch, useAppSelector } from '~/store';
import { actionsClanProfile, changeRole } from '~/Actions/ActionClanProfile';

import { RoleSelect } from '~/UIKit';

import MembersOperationDialog from './MembersOperationDialog';
import styles from './MembersOperationDialog.scss';
import RolesDescription from './RolesDescription';

import type { AppStateType as RootState } from '~/Reducers';
import type { ROLE_NAMES } from '~/roles';
import type { IClanMember, IDialog } from '~/types/declaration';

const stateSelector = (state: RootState) => {
  const clanId = state.currentAccount.clanId;

  return {
    currentAccountRoleName: state.currentAccount.roleName,
    members: clanId ? state.members.clans[clanId]?.members || [] : [],
    selectedIds: state.membersOperation.selectedIds,
    selectedRole: state.membersOperation.selectedRole,
  };
};

type IChangeRoleOperationDialog = IDialog<{
  memberToChangeId?: number;
}>;

const ChangeRoleOperationDialog = ({ data, hideDialog }: IChangeRoleOperationDialog) => {
  const memberToChangeId = data?.memberToChangeId;

  const { currentAccountRoleName, members, selectedIds, selectedRole } = useAppSelector(stateSelector, shallowEqual);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!hasPermission(currentAccountRoleName, PERMISSIONS.CHANGE_ROLE)) {
      dispatch(actionsClanProfile.clearMembersOperation());
      hideDialog();
    }
  }, [currentAccountRoleName, dispatch, hideDialog]);

  const onChangeClick = useCallback(async () => {
    await dispatch(changeRole(memberToChangeId));
    hideDialog();
  }, [dispatch, hideDialog, memberToChangeId]);

  const onRoleSelect = useCallback(
    (newRole: ROLE_NAMES) => {
      dispatch(actionsClanProfile.selectRoleMembersOperation(newRole));
    },
    [dispatch],
  );

  const onDialogClose = useCallback(() => {
    hideDialog();
    dispatch(actionsClanProfile.clearMembersOperation());
  }, [dispatch, hideDialog]);

  const selectedMembers = useMemo<IClanMember[]>(() => {
    const ids = memberToChangeId !== undefined ? [memberToChangeId] : selectedIds;
    if (!ids?.length) {
      return [];
    }
    return members.filter((m) => ids.includes(m.id));
  }, [memberToChangeId, members, selectedIds]);

  const rolesToChange = useMemo<ROLE_NAMES[]>(() => {
    return currentAccountRoleName ? getAssignableRoles(currentAccountRoleName) : [];
  }, [currentAccountRoleName]);

  const [neverSelected, setNeverSelected] = useState(true);

  useEffect(() => {
    setNeverSelected(true);
  }, [memberToChangeId, selectedIds]);

  const handleRoleSelect = useCallback(
    (role: ROLE_NAMES) => {
      onRoleSelect(role);
      setNeverSelected(false);
    },
    [onRoleSelect],
  );

  const currentRole: ROLE_NAMES | null = useMemo(() => {
    if (!selectedMembers.length) {
      return null;
    }

    const isSameRole = selectedMembers.every((member) => member.roleName === selectedMembers[0].roleName);

    return isSameRole ? selectedMembers[0].roleName : null;
  }, [selectedMembers]);

  const roleSelect = (
    <div>
      <RoleSelect roles={rolesToChange} selected={selectedRole} current={currentRole} onChange={handleRoleSelect} />
      <div className={styles.helperIcon}>
        <HelperIcon header={t('Должности и полномочия')}>
          <RolesDescription />
        </HelperIcon>
      </div>
    </div>
  );

  return (
    <MembersOperationDialog
      buttonActionTitle={t('Изменить')}
      buttonCloseTitle={t('Закрыть')}
      controlElement={roleSelect}
      membersForOperate={selectedMembers}
      spinnerLabel={t('Изменяем должность')}
      textBeforeMembers={t('Изменить роль игрокам:')}
      textAfterMembers={t('Назначить на должность:')}
      title={t('Смена должности')}
      buttonActionDisabled={neverSelected}
      onActionClick={onChangeClick}
      onCloseClick={onDialogClose}
    />
  );
};

export default memo(ChangeRoleOperationDialog);
